import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { UtilityService } from '../utility.service';
import { FormSearch } from 'app/models/dashboard/FormStatus';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';
import { environment } from 'environments/environment';
import { TaxingJurisdictionView } from 'app/models/purchasing/TaxingJurisdictionView';
import {
	TaxingJurisdictionImportRow,
	TaxingJurisdictionImportResult,
	TaxingJurisdictionImportValidationResult,
} from 'app/models/purchasing/TaxingJurisdictionImport';

@Injectable({
	providedIn: 'root',
})
export class TaxingJurisdictionService {
	headers = new HttpHeaders({ 'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0' });

	private readonly urlPath = `${environment.apiUrl}forms/clients/`;

	constructor(private httpClient: HttpClient, private utilityService: UtilityService) {}

	createForm(clientId: string, request: any): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/taxing-jurisdiction-form`, this.setupRequest(request))
			.pipe();
	}

	private setupRequest(request: any) {
		const formData: FormData = new FormData();
		formData.append('model', JSON.stringify(request));
		return formData;
	}

	updateForm(clientId: string, id: string, request: any): Observable<any> {
		return this.httpClient
			.put<any>(`${this.urlPath}${clientId}/taxing-jurisdiction-form/${id}`, this.setupRequest(request))
			.pipe();
	}
	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(`${this.urlPath}${clientId}/taxing-jurisdiction-form/${formId}/docs/${docId}`);
	}
	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/taxing-jurisdiction-form/${id}`).pipe();
	}

	getAllForms(clientId: string, request: any): Observable<any[]> {
		return this.httpClient.post<any[]>(`${this.urlPath}${clientId}/taxing-jurisdiction-form/all`, request).pipe();
	}
	rejectForm(clientId: string, id: string, rejectionReason: { reason: string; workflowId: number }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/taxing-jurisdiction-form/${id}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, id: string, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/taxing-jurisdiction-form/${id}/approve`, {
				userId,
			})
			.pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		let url = `${
			this.urlPath
		}${clientId}/taxing-jurisdiction-form/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	sendBack(clientId: string, id: string): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/taxing-jurisdiction-form/${id}/review`, {}).pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest('POST', `${this.urlPath}${clientId}/taxing-jurisdiction-form/${id}/docs`, formData, {
			reportProgress: true,
			responseType: 'json',
		});
		return this.httpClient.request(req);
	}

	deleteForm(clientId: string, id: string) {
		return this.httpClient.delete(`${this.urlPath}${clientId}/taxing-jurisdiction-form/${id}`).pipe();
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<TaxingJurisdictionView> {
		return this.httpClient
			.post<TaxingJurisdictionView>(`${this.urlPath}${clientId}/taxing-jurisdiction-form/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	validateImport(
		clientId: string,
		rows: TaxingJurisdictionImportRow[]
	): Observable<TaxingJurisdictionImportValidationResult> {
		return this.httpClient.post<TaxingJurisdictionImportValidationResult>(
			`${this.urlPath}${clientId}/taxing-jurisdiction-form/import/validate`,
			rows
		);
	}

	confirmImport(clientId: string, rows: TaxingJurisdictionImportRow[]): Observable<TaxingJurisdictionImportResult> {
		return this.httpClient.post<TaxingJurisdictionImportResult>(
			`${this.urlPath}${clientId}/taxing-jurisdiction-form/import`,
			rows
		);
	}
}
